import { FC, useEffect, useState } from "react";
import { useFormikContext } from "formik";
import * as Yup from "yup";
import InputField from "@/components/form/InputField";
import SelectField from "@/components/form/SelectField";
import InputDisplayOptions from "@/components/data-model/InputDisplayOptions";
import SelectDisplayOptions from "@/components/data-model/SelectDisplayOptions";
import TextAreaDisplayOptions from "@/components/data-model/TextAreaDisplayOptions";
import RichTextDisplayOptions from "@/components/data-model/RichTextDisplayOptions";
import FileInputDisplayOptions from "@/components/data-model/FileInputDisplayOptions";
import { useEntities } from "@/hooks/useCommonQueries";
import { buildFieldValidationSchema } from "@/components/data-model/fieldValidationBuilder";
import {
  CreateField,
  StandardFieldDatabaseType,
  RelationshipFieldDatabaseType,
  FileFieldDatabaseType,
  UserFieldDatabaseType,
  FIELD_TYPE_GROUPS,
  isRelationshipFieldType,
  StandardFieldDisplayType,
  FileFieldDisplayType,
  RelationshipFieldDisplayType,
  UserFieldDisplayType,
  SecretFieldDatabaseType,
  SecretFieldDisplayType,
  isDynamicReferenceFieldType,
  isRelationshipOrFileOrUserFieldType,
} from "@/types/breeze/Field";
import { Box, Card, Flex, Text, Grid, Tabs, Heading, SegmentedControl } from "@radix-ui/themes";
import CheckboxField from "@/components/form/CheckboxField";
import SearchableSelect from "@/components/ui/SearchableSelect";
import {
  Type,
  AlignLeft,
  Hash,
  Binary,
  Coins,
  ToggleLeft,
  Calendar,
  Clock,
  Braces,
  MapPin,
  ArrowRightLeft,
  GitFork,
  Merge,
  Network,
  Link2,
  FileUp,
  User,
  Key,
  Check,
  List,
} from "lucide-react";

interface IProps {
  entityName: string;
  submitting: boolean;
  isUpdate?: boolean;
  onValidationSchemaChange?: (schema: Yup.ObjectSchema<any>) => void;
}

const FIELD_TYPE_METADATA: Record<
  string,
  {
    label: string;
    description: string;
    icon: React.ComponentType<any>;
    color: string;
  }
> = {
  [StandardFieldDatabaseType.VARCHAR]: {
    label: "Small text",
    description: "For names, titles, codes, short strings",
    icon: Type,
    color: "blue",
  },
  [StandardFieldDatabaseType.TEXT]: {
    label: "Large text",
    description: "For descriptions, articles, markdown content",
    icon: AlignLeft,
    color: "blue",
  },
  [StandardFieldDatabaseType.INTEGER]: {
    label: "Integer",
    description: "Whole numbers, counters, or identifiers",
    icon: Hash,
    color: "indigo",
  },
  [StandardFieldDatabaseType.BIGINTEGER]: {
    label: "Big Integer",
    description: "Very large integers, epoch times",
    icon: Binary,
    color: "indigo",
  },
  [StandardFieldDatabaseType.DECIMAL]: {
    label: "Decimal",
    description: "Prices, coordinates, fractional numbers",
    icon: Coins,
    color: "indigo",
  },
  [StandardFieldDatabaseType.BOOLEAN]: {
    label: "Boolean",
    description: "Yes/No, True/False, flags",
    icon: ToggleLeft,
    color: "teal",
  },
  [StandardFieldDatabaseType.DATE]: {
    label: "Date",
    description: "Calendar dates without timezones",
    icon: Calendar,
    color: "sky",
  },
  [StandardFieldDatabaseType.TIMESTAMP]: {
    label: "Timestamp",
    description: "Precise date and time with zone",
    icon: Clock,
    color: "sky",
  },
  [StandardFieldDatabaseType.JSON]: {
    label: "JSON",
    description: "Structured objects, arrays, flexible data",
    icon: Braces,
    color: "amber",
  },
  [StandardFieldDatabaseType.GEO]: {
    label: "Geo",
    description: "Geographical coordinates, points",
    icon: MapPin,
    color: "amber",
  },
  [RelationshipFieldDatabaseType.ONE_TO_ONE]: {
    label: "One-to-One",
    description: "Links one record to exactly one other record",
    icon: ArrowRightLeft,
    color: "violet",
  },
  [RelationshipFieldDatabaseType.ONE_TO_MANY]: {
    label: "One-to-Many",
    description: "One record links to multiple target records",
    icon: GitFork,
    color: "violet",
  },
  [RelationshipFieldDatabaseType.MANY_TO_ONE]: {
    label: "Many-to-One",
    description: "Multiple records link to a single target record",
    icon: Merge,
    color: "violet",
  },
  [RelationshipFieldDatabaseType.MANY_TO_MANY]: {
    label: "Many-to-Many",
    description: "Multiple records link to multiple other records",
    icon: Network,
    color: "violet",
  },
  [RelationshipFieldDatabaseType.DYNAMIC_REFERENCE]: {
    label: "Dynamic Reference",
    description: "Polymorphic relations to different entities",
    icon: Link2,
    color: "violet",
  },
  [FileFieldDatabaseType.FILE]: {
    label: "File Upload",
    description: "Documents, images, or media files",
    icon: FileUp,
    color: "emerald",
  },
  [UserFieldDatabaseType.USER]: {
    label: "User",
    description: "Links records directly to system users",
    icon: User,
    color: "pink",
  },
  [SecretFieldDatabaseType.SECRET]: {
    label: "Secret",
    description: "API keys, hashed credentials, passwords",
    icon: Key,
    color: "red",
  },
};

const DISPLAY_TYPE_METADATA: Record<
  string,
  {
    label: string;
    description: string;
    icon: React.ComponentType<any>;
    color: string;
  }
> = {
  [StandardFieldDisplayType.INPUT]: {
    label: "Input",
    description: "Single-line text input field",
    icon: Type,
    color: "blue",
  },
  [StandardFieldDisplayType.SELECT]: {
    label: "Select",
    description: "Dropdown list with predefined options",
    icon: List,
    color: "indigo",
  },
  [StandardFieldDisplayType.TEXTAREA]: {
    label: "Textarea",
    description: "Multi-line text input for longer content",
    icon: AlignLeft,
    color: "blue",
  },
  [StandardFieldDisplayType.RICH_TEXT]: {
    label: "Rich Text",
    description: "WYSIWYG editor supporting styles/markdown",
    icon: AlignLeft,
    color: "teal",
  },
  [StandardFieldDisplayType.CHECKBOX]: {
    label: "Checkbox",
    description: "Simple box that can be toggled on/off",
    icon: Check,
    color: "teal",
  },
  [StandardFieldDisplayType.RADIO]: {
    label: "Radio",
    description: "Select one option from a visible list",
    icon: ToggleLeft,
    color: "teal",
  },
  [StandardFieldDisplayType.SHORT_DATE]: {
    label: "Short Date",
    description: "Date picker for calendar day",
    icon: Calendar,
    color: "sky",
  },
  [StandardFieldDisplayType.LONG_DATE]: {
    label: "Long Date",
    description: "Date picker with more readable format",
    icon: Calendar,
    color: "sky",
  },
  [StandardFieldDisplayType.TIMESTAMP]: {
    label: "Timestamp",
    description: "Precise date and time picker",
    icon: Clock,
    color: "sky",
  },
  [StandardFieldDisplayType.JSON]: {
    label: "JSON",
    description: "Raw JSON structure editor",
    icon: Braces,
    color: "amber",
  },
  [StandardFieldDisplayType.GEO]: {
    label: "Geo",
    description: "Geographical coordinates/point selector",
    icon: MapPin,
    color: "amber",
  },
  [FileFieldDisplayType.FILE]: {
    label: "File",
    description: "File upload input",
    icon: FileUp,
    color: "emerald",
  },
  [UserFieldDisplayType.USER]: {
    label: "User",
    description: "User account selection dropdown",
    icon: User,
    color: "pink",
  },
  [RelationshipFieldDisplayType.RELATIONSHIP]: {
    label: "Relationship",
    description: "Link to associated entity records",
    icon: ArrowRightLeft,
    color: "violet",
  },
  [RelationshipFieldDisplayType.DYNAMIC_REFERENCE]: {
    label: "Dynamic Reference",
    description: "Dynamic link to other entity records",
    icon: Link2,
    color: "violet",
  },
  [SecretFieldDisplayType.SECRET]: {
    label: "Secret",
    description: "Encrypted password or API key input",
    icon: Key,
    color: "red",
  },
};

const CreateUpdateFieldForm: FC<IProps> = ({
  entityName,
  isUpdate = false,
  onValidationSchemaChange,
}) => {
  const { data: entities } = useEntities();
  const { values, setFieldValue, errors, touched } = useFormikContext<CreateField>();
  const [isCreatingNewField, setIsCreatingNewField] = useState(false);

  useEffect(() => {
    if (!onValidationSchemaChange) return;

    // Build complete validation schema using our validation builder
    const validationSchema = buildFieldValidationSchema(
      values.display_type,
      values.database_type,
      values.is_required,
    );

    onValidationSchemaChange(validationSchema);
  }, [
    values.display_type,
    values.database_type,
    values.is_required,
    onValidationSchemaChange,
  ]);

  // Handle OnDeletion value when field requirement changes
  useEffect(() => {
    if (values.is_required && values.relationship?.on_deletion === "SET NULL") {
      // If field becomes required and SET NULL is selected, change to CASCADE as default
      setFieldValue("relationship.on_deletion", "CASCADE");
    }
  }, [values.is_required, values.relationship?.on_deletion, setFieldValue]);

  useEffect(() => {
    if (values.is_name_field) {
      setFieldValue("is_searchable", true);
    }

    if (!values.is_searchable) {
      setFieldValue("publicly_searchable", false);
    }
  }, [values.is_name_field, values.is_searchable, setFieldValue]);

  // Reset creating new field state when target entity changes
  useEffect(() => {
    if (values.relationship?.target_entity_id) {
      setIsCreatingNewField(false);
      setFieldValue("relationship.auto_create_related_field", false);
    }
  }, [values.relationship?.target_entity_id, setFieldValue]);

  // Handle auto-create field functionality
  const handleCreateNewFieldToggle = (shouldCreate: boolean) => {
    setIsCreatingNewField(shouldCreate);
    if (shouldCreate) {
      // Clear existing target field name and set auto_create_related_field to true
      setFieldValue("relationship.target_field_name", "");
      setFieldValue("relationship.auto_create_related_field", true);
    } else {
      // Reset auto_create_related_field to false
      setFieldValue("relationship.auto_create_related_field", false);
    }
  };

  const handleDatabaseTypeChange = (database_type: string) => {
    let display_type = "";
    let initialOptions = null;

    switch (database_type) {
      case StandardFieldDatabaseType.VARCHAR:
      case StandardFieldDatabaseType.INTEGER:
      case StandardFieldDatabaseType.BIGINTEGER:
      case StandardFieldDatabaseType.DECIMAL:
        display_type = StandardFieldDisplayType.INPUT;
        initialOptions = {
          input: {
            addon_before: undefined,
            addon_after: undefined,
            placeholder: undefined,
            masked: false,
          },
          select: null,
          textarea: null,
          rich_text: null,
          file: null,
        };
        break;
      case StandardFieldDatabaseType.TEXT:
        display_type = StandardFieldDisplayType.RICH_TEXT;
        initialOptions = {
          input: null,
          select: null,
          textarea: null,
          rich_text: {
            rows: 3,
            enable_markdown: false,
          },
          file: null,
        };
        break;
      case StandardFieldDatabaseType.BOOLEAN:
        display_type = StandardFieldDisplayType.CHECKBOX;
        break;
      case StandardFieldDatabaseType.DATE:
        display_type = StandardFieldDisplayType.LONG_DATE;
        initialOptions = {
          input: {
            addon_before: undefined,
            addon_after: undefined,
            placeholder: undefined,
            masked: false,
          },
          select: null,
          textarea: null,
          rich_text: null,
          file: null,
        };
        break;
      case StandardFieldDatabaseType.TIMESTAMP:
        display_type = StandardFieldDisplayType.TIMESTAMP;
        initialOptions = {
          input: {
            addon_before: undefined,
            addon_after: undefined,
            placeholder: undefined,
            masked: false,
          },
          select: null,
          textarea: null,
          rich_text: null,
          file: null,
        };
        break;
      case FileFieldDatabaseType.FILE:
        display_type = FileFieldDisplayType.FILE;
        initialOptions = {
          input: null,
          select: null,
          textarea: null,
          rich_text: null,
          file: {
            accept: "*/*",
            max_files: 1,
            max_size: 10485760, // 10MB
            public: false,
          },
        };
        break;
      case UserFieldDatabaseType.USER:
        display_type = UserFieldDisplayType.USER;
        break;
      case SecretFieldDatabaseType.SECRET:
        display_type = SecretFieldDisplayType.SECRET;
        break;
      case StandardFieldDatabaseType.JSON:
        display_type = StandardFieldDisplayType.JSON;
        break;
      case StandardFieldDatabaseType.GEO:
        display_type = StandardFieldDisplayType.GEO;
        break;
      case RelationshipFieldDatabaseType.ONE_TO_ONE:
      case RelationshipFieldDatabaseType.ONE_TO_MANY:
      case RelationshipFieldDatabaseType.MANY_TO_ONE:
      case RelationshipFieldDatabaseType.MANY_TO_MANY:
        display_type = RelationshipFieldDisplayType.RELATIONSHIP;
        break;
      case RelationshipFieldDatabaseType.DYNAMIC_REFERENCE:
        display_type = RelationshipFieldDisplayType.DYNAMIC_REFERENCE;
        break;
      default:
        display_type = StandardFieldDisplayType.INPUT;
        initialOptions = {
          input: {
            addon_before: undefined,
            addon_after: undefined,
            placeholder: undefined,
            masked: false,
          },
          select: null,
          textarea: null,
          rich_text: null,
          file: null,
        };
        break;
    }

    setFieldValue("display_type", display_type);
    setFieldValue("options", initialOptions);

    // Initialize relationship object for File and User field types
    if (
      database_type === FileFieldDatabaseType.FILE ||
      database_type === UserFieldDatabaseType.USER
    ) {
      setFieldValue("relationship", {
        target_entity_id: undefined,
        target_field_name: undefined,
        on_deletion: "CASCADE",
        auto_create_related_field: false,
      });
    } else if (isRelationshipFieldType(database_type)) {
      // Initialize relationship object for relationship field types
      setFieldValue("relationship", {
        target_entity_id: undefined,
        target_field_name: undefined,
        on_deletion: "CASCADE",
        auto_create_related_field: false,
      });
    } else if (
      database_type !== RelationshipFieldDatabaseType.DYNAMIC_REFERENCE
    ) {
      // Clear relationship object for non-relationship field types
      setFieldValue("relationship", undefined);
    }

    // Reset the creating new field state when database type changes
    setIsCreatingNewField(false);
    setFieldValue("database_type", database_type);
  };

  const handleDisplayTypeChange = (display_type: string) => {
    let initialOptions = null;

    switch (display_type) {
      case StandardFieldDisplayType.INPUT:
      case StandardFieldDisplayType.SHORT_DATE:
      case StandardFieldDisplayType.LONG_DATE:
      case StandardFieldDisplayType.TIMESTAMP:
        initialOptions = {
          input: {
            addon_before: undefined,
            addon_after: undefined,
            placeholder: undefined,
            masked: false,
          },
          select: null,
          textarea: null,
          rich_text: null,
          file: null,
        };
        break;
      case StandardFieldDisplayType.SELECT:
        initialOptions = {
          input: null,
          select: {
            addon_before: undefined,
            addon_after: undefined,
            placeholder: undefined,
            multiple: false,
            options: [],
          },
          textarea: null,
          rich_text: null,
          file: null,
        };
        break;
      case StandardFieldDisplayType.TEXTAREA:
        initialOptions = {
          input: null,
          select: null,
          textarea: {
            rows: 3,
          },
          rich_text: null,
          file: null,
        };
        break;
      case StandardFieldDisplayType.RICH_TEXT:
        initialOptions = {
          input: null,
          select: null,
          textarea: null,
          rich_text: {
            rows: 3,
            enable_markdown: false,
          },
          file: null,
        };
        break;
      case FileFieldDisplayType.FILE:
        initialOptions = {
          input: null,
          select: null,
          textarea: null,
          rich_text: null,
          file: {
            accept: "*/*",
            max_files: 1,
            max_size: 10485760, // 10MB
            public: false,
          },
        };
        break;
    }

    setFieldValue("display_type", display_type);
    if (initialOptions) {
      setFieldValue("options", initialOptions);
    }
  };

  const getValidDisplayTypes = (): string[] => {
    const dbType = values.database_type;
    if (
      [
        StandardFieldDatabaseType.VARCHAR,
        StandardFieldDatabaseType.INTEGER,
        StandardFieldDatabaseType.BIGINTEGER,
        StandardFieldDatabaseType.DECIMAL,
      ].includes(dbType as any)
    ) {
      return [StandardFieldDisplayType.INPUT, StandardFieldDisplayType.SELECT];
    }
    if (dbType === StandardFieldDatabaseType.TEXT) {
      return [
        StandardFieldDisplayType.TEXTAREA,
        StandardFieldDisplayType.RICH_TEXT,
      ];
    }
    if (dbType === StandardFieldDatabaseType.BOOLEAN) {
      return [
        StandardFieldDisplayType.CHECKBOX,
        StandardFieldDisplayType.RADIO,
      ];
    }
    if (dbType === StandardFieldDatabaseType.DATE) {
      return [
        StandardFieldDisplayType.SHORT_DATE,
        StandardFieldDisplayType.LONG_DATE,
      ];
    }
    if (dbType === StandardFieldDatabaseType.TIMESTAMP) {
      return [StandardFieldDisplayType.TIMESTAMP];
    }
    if (dbType === StandardFieldDatabaseType.JSON) {
      return [StandardFieldDisplayType.JSON];
    }
    if (dbType === StandardFieldDatabaseType.GEO) {
      return [StandardFieldDisplayType.GEO];
    }
    if (dbType === FileFieldDatabaseType.FILE) {
      return [FileFieldDisplayType.FILE];
    }
    if (dbType === UserFieldDatabaseType.USER) {
      return [UserFieldDisplayType.USER];
    }
    if (isRelationshipFieldType(dbType)) {
      return [RelationshipFieldDisplayType.RELATIONSHIP];
    }
    if (isDynamicReferenceFieldType(dbType)) {
      return [RelationshipFieldDisplayType.DYNAMIC_REFERENCE];
    }
    if (dbType === SecretFieldDatabaseType.SECRET) {
      return [SecretFieldDisplayType.SECRET];
    }
    return [StandardFieldDisplayType.INPUT];
  };

  const getDisplayOptionsComponent = () => {
    switch (values.display_type) {
      case StandardFieldDisplayType.INPUT:
      case StandardFieldDisplayType.SHORT_DATE:
      case StandardFieldDisplayType.LONG_DATE:
      case StandardFieldDisplayType.TIMESTAMP:
        return <InputDisplayOptions />;
      case StandardFieldDisplayType.SELECT:
        return <SelectDisplayOptions />;
      case StandardFieldDisplayType.TEXTAREA:
        return <TextAreaDisplayOptions />;
      case StandardFieldDisplayType.RICH_TEXT:
        return <RichTextDisplayOptions />;
      case FileFieldDisplayType.FILE:
        return <FileInputDisplayOptions />;
      default:
        return null;
    }
  };

  return (
    <Flex direction="column" gap="4">
      <Card size="2">
        <Box mb="4">
          <Text size="4" weight="medium">
            Field Configuration
          </Text>
        </Box>

        <Flex direction="column" gap="3">
          <Box mb="2">
            {isUpdate ? (
              <Card
                size="1"
                style={{
                  border: "1px solid var(--gray-6)",
                  backgroundColor: "var(--gray-2)",
                  padding: "12px",
                }}
              >
                <Flex gap="3" align="center">
                  <Flex
                    align="center"
                    justify="center"
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "var(--radius-2)",
                      backgroundColor: `var(--${
                        FIELD_TYPE_METADATA[values.database_type]?.color ||
                        "gray"
                      }-3)`,
                      color: `var(--${
                        FIELD_TYPE_METADATA[values.database_type]?.color ||
                        "gray"
                      }-9)`,
                    }}
                  >
                    {(() => {
                      const IconComponent =
                        FIELD_TYPE_METADATA[values.database_type]?.icon || Type;
                      return <IconComponent size={20} />;
                    })()}
                  </Flex>
                  <Box style={{ flexGrow: 1 }}>
                    <Flex gap="2" align="center">
                      <Text weight="bold" size="2">
                        {FIELD_TYPE_METADATA[values.database_type]?.label ||
                          values.database_type}
                      </Text>
                    </Flex>
                    <Text size="1" color="gray" as="p">
                      {FIELD_TYPE_METADATA[values.database_type]?.description ||
                        "Database field type."}
                    </Text>
                  </Box>
                </Flex>
              </Card>
            ) : (
              <Tabs.Root
                defaultValue={
                  Object.keys(FIELD_TYPE_GROUPS).find((group) =>
                    FIELD_TYPE_GROUPS[
                      group as keyof typeof FIELD_TYPE_GROUPS
                    ].some((t) => t.value === values.database_type),
                  ) || "Standard"
                }
              >
                <Tabs.List size="1">
                  {Object.keys(FIELD_TYPE_GROUPS).map((group) => (
                    <Tabs.Trigger key={group} value={group}>
                      {group}
                    </Tabs.Trigger>
                  ))}
                </Tabs.List>
                <Box pt="3">
                  {Object.entries(FIELD_TYPE_GROUPS).map(([group, types]) => (
                    <Tabs.Content key={group} value={group}>
                      <Grid columns="2" gap="2">
                        {types.map(({ value }) => {
                          const meta = FIELD_TYPE_METADATA[value];
                          if (!meta) return null;
                          const Icon = meta.icon;
                          const isSelected = values.database_type === value;
                          return (
                            <Card
                              key={value}
                              style={{
                                cursor: "pointer",
                                border: isSelected
                                  ? "2px solid var(--accent-9)"
                                  : "1px solid var(--gray-5)",
                                backgroundColor: isSelected
                                  ? "var(--accent-2)"
                                  : "transparent",
                                transition: "all 0.2s ease",
                                padding: "10px",
                              }}
                              onClick={() => handleDatabaseTypeChange(value)}
                            >
                              <Flex gap="3" align="center">
                                <Flex
                                  align="center"
                                  justify="center"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "var(--radius-2)",
                                    backgroundColor: `var(--${meta.color}-3)`,
                                    color: `var(--${meta.color}-9)`,
                                    flexShrink: 0,
                                  }}
                                >
                                  <Icon size={16} />
                                </Flex>
                                <Box style={{ flexGrow: 1, minWidth: 0 }}>
                                  <Text
                                    weight="bold"
                                    size="2"
                                    style={{
                                      display: "block",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {meta.label}
                                  </Text>
                                  <Text
                                    size="1"
                                    color="gray"
                                    style={{
                                      display: "block",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {meta.description}
                                  </Text>
                                </Box>
                                {isSelected && (
                                  <Flex
                                    align="center"
                                    justify="center"
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      borderRadius: "50%",
                                      backgroundColor: "var(--accent-9)",
                                      color: "white",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Check size={10} />
                                  </Flex>
                                )}
                              </Flex>
                            </Card>
                          );
                        })}
                      </Grid>
                    </Tabs.Content>
                  ))}
                </Box>
              </Tabs.Root>
            )}
          </Box>

          {isRelationshipFieldType(values.database_type) && (
            <Box>
              <Box mb="4">
                <Text as="label" size="2" weight="medium" mb="2" style={{ display: "block" }}>
                  Related Entity {!isUpdate && <Text color="red">*</Text>}
                </Text>
                <SearchableSelect
                  value={values.relationship?.target_entity_id?.toString() || ""}
                  onChange={(val) => {
                    setFieldValue("relationship.target_entity_id", parseInt(val, 10));
                  }}
                  disabled={isUpdate}
                  options={
                    entities
                      ?.filter((e) => e.name !== entityName && !e.is_junction)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((entity) => ({
                        value: entity.id.toString(),
                        label: entity.name,
                      })) || []
                  }
                  placeholder="Select an entity..."
                  searchPlaceholder="Search entities..."
                />
                {((errors.relationship as any)?.target_entity_id) && ((touched.relationship as any)?.target_entity_id) && (
                  <Text color="red" size="1" mt="1" style={{ display: "block" }}>
                    {(errors.relationship as any).target_entity_id}
                  </Text>
                )}
              </Box>

              {values.database_type ===
                RelationshipFieldDatabaseType.ONE_TO_MANY && (
                <Box>
                  <SelectField
                    name="relationship.target_field_name"
                    label="Foreign Key"
                    required={!isCreatingNewField}
                    disabled={isUpdate || isCreatingNewField}
                    type="string"
                    emptyText="No foreign key fields available"
                    onChange={(value) => {
                      if (value === "__CREATE_NEW__") {
                        handleCreateNewFieldToggle(true);
                      } else {
                        handleCreateNewFieldToggle(false);
                        setFieldValue("relationship.target_field_name", value);
                      }
                    }}
                  >
                    {entities
                      ?.find(
                        (e) => e.id === values.relationship?.target_entity_id,
                      )
                      ?.fields.filter(
                        (field) =>
                          field.database_type ===
                          RelationshipFieldDatabaseType.MANY_TO_ONE,
                      )
                      .map((field) => (
                        <SelectField.Item key={field.id} value={field.name}>
                          {field.name}
                        </SelectField.Item>
                      ))}
                    <hr />
                    <SelectField.Item value="__CREATE_NEW__">
                      + Create New Target Field
                    </SelectField.Item>
                  </SelectField>

                  {isCreatingNewField && (
                    <Box mt="3">
                      <InputField
                        name="relationship.target_field_name"
                        label="New Target Field Name"
                        options={{
                          placeholder: `Enter target field name (e.g., ${entityName}_id)`,
                          addon_before: undefined,
                          addon_after: undefined,
                          masked: false,
                        }}
                        required
                      />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}

          {isRelationshipOrFileOrUserFieldType(values.database_type) && (
            <SelectField
              name="relationship.on_deletion"
              label="On Deletion"
              required
              type="string"
            >
              <SelectField.Item value="CASCADE">Cascade</SelectField.Item>
              {!values.is_required && (
                <SelectField.Item value="SET NULL">Set Null</SelectField.Item>
              )}
              <SelectField.Item value="SET DEFAULT">
                Set Default
              </SelectField.Item>
              <SelectField.Item value="RESTRICT">Restrict</SelectField.Item>
            </SelectField>
          )}

          <InputField name="name" label="Name" required disabled={isUpdate} />

          <InputField name="label" label="Label" />

          <InputField
            name="description"
            label="Description"
            as="textarea"
            options={{ rows: 3 }}
          />

          <InputField name="default_value" label="Default Value" />

          <Grid columns="2" gap="4" mt="0">
            <Card variant="ghost">
              <Heading size="2" mb="3" color="gray">
                Validation & Integrity
              </Heading>
              <Flex direction="column" gap="3">
                <CheckboxField
                  name="is_required"
                  label="Required field"
                  description="Value cannot be null or empty."
                  disabled={isUpdate}
                  marginBottom={false}
                />
                <CheckboxField
                  name="is_unique"
                  label="Unique field"
                  description="Value must be unique across records."
                  marginBottom={false}
                />
                <CheckboxField
                  name="is_immutable"
                  label="Immutable field"
                  description="Value cannot be updated once saved."
                  marginBottom={false}
                />
              </Flex>
            </Card>

            <Card variant="ghost">
              <Heading size="2" mb="3" color="gray">
                Performance & API
              </Heading>
              <Flex direction="column" gap="3">
                <CheckboxField
                  name="is_name_field"
                  label="Name field"
                  description="Identifies the record visually."
                  marginBottom={false}
                />
                <CheckboxField
                  name="is_indexed"
                  label="Indexed"
                  description="Creates an index in the database."
                  marginBottom={false}
                />
                <CheckboxField
                  name="is_searchable"
                  label="Searchable"
                  description="Searchable in API queries."
                  marginBottom={false}
                />
                <CheckboxField
                  name="publicly_searchable"
                  label="Publicly Searchable"
                  description="Allows public user searches."
                  disabled={!values.is_searchable}
                  marginBottom={false}
                />
              </Flex>
            </Card>
          </Grid>
        </Flex>
      </Card>

      <Card size="2">
        <Box mb="4">
          <Text size="4" weight="medium">
            Display Configuration
          </Text>
        </Box>

        <Flex direction="column" gap="3">
          <Box mb="2">
            <Grid columns="2" gap="2">
              {getValidDisplayTypes().map((type) => {
                const meta = DISPLAY_TYPE_METADATA[type];
                if (!meta) return null;
                const Icon = meta.icon;
                const isSelected = values.display_type === type;
                const isSingleOption = getValidDisplayTypes().length === 1;
                return (
                  <Card
                    key={type}
                    style={{
                      cursor: isSingleOption ? "default" : "pointer",
                      border: isSelected
                        ? "2px solid var(--accent-9)"
                        : "1px solid var(--gray-5)",
                      backgroundColor: isSelected
                        ? "var(--accent-2)"
                        : "transparent",
                      transition: "all 0.2s ease",
                      padding: "10px",
                      opacity: isSingleOption ? 0.9 : 1,
                    }}
                    onClick={() => {
                      if (!isSingleOption) {
                        handleDisplayTypeChange(type);
                      }
                    }}
                  >
                    <Flex gap="3" align="center">
                      <Flex
                        align="center"
                        justify="center"
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "var(--radius-2)",
                          backgroundColor: `var(--${meta.color}-3)`,
                          color: `var(--${meta.color}-9)`,
                          flexShrink: 0,
                        }}
                      >
                        <Icon size={16} />
                      </Flex>
                      <Box style={{ flexGrow: 1, minWidth: 0 }}>
                        <Text
                          weight="bold"
                          size="2"
                          style={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {meta.label}
                        </Text>
                        <Text
                          size="1"
                          color="gray"
                          style={{
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {meta.description}
                        </Text>
                      </Box>
                      {isSelected && (
                        <Flex
                          align="center"
                          justify="center"
                          style={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            backgroundColor: "var(--accent-9)",
                            color: "white",
                            flexShrink: 0,
                          }}
                        >
                          <Check size={10} />
                        </Flex>
                      )}
                    </Flex>
                  </Card>
                );
              })}
            </Grid>
          </Box>

          <Box mb="2">
            <Text
              as="label"
              size="2"
              weight="medium"
              mb="2"
              style={{ display: "block" }}
            >
              Width
            </Text>
            <SegmentedControl.Root
              value={values.width.toString()}
              onValueChange={(val) => setFieldValue("width", parseInt(val, 10))}
              style={{ width: "100%" }}
            >
              <SegmentedControl.Item value="50">
                <Flex align="center" gap="2" justify="center">
                  <Box
                    style={{
                      width: 12,
                      height: 8,
                      border: "1px dashed var(--gray-8)",
                      borderRadius: 1,
                    }}
                  />
                  Half Width (50%)
                </Flex>
              </SegmentedControl.Item>
              <SegmentedControl.Item value="100">
                <Flex align="center" gap="2" justify="center">
                  <Box
                    style={{
                      width: 24,
                      height: 8,
                      border: "1px dashed var(--gray-8)",
                      borderRadius: 1,
                    }}
                  />
                  Full Width (100%)
                </Flex>
              </SegmentedControl.Item>
            </SegmentedControl.Root>
          </Box>

          {getDisplayOptionsComponent()}
        </Flex>
      </Card>
    </Flex>
  );
};

export default CreateUpdateFieldForm;
